import { APIService } from '../../api/apiService';
import type {
  CheckFolderPasswordResponse,
  DeleteFolderPasswordPayload,
  DeleteFolderPasswordResponse,
  DeleteFolderPasswordSessionResponse,
  FolderPasswordInfo,
  GetFolderPasswordSessionsResponse,
  ResetFolderPasswordPayload,
  SetFolderPasswordPayload,
  UpdateFolderPasswordPayload,
  VerifyFolderPasswordPayload,
  VerifyFolderPasswordResponse,
} from '../../interfaces/FoldersApiService';

export class FoldersService extends APIService {
  /**
   * Creates a password for a folder.
   * @param folderId - The unique identifier of the folder.
   * @param payload - The password and settings to set for the folder.
   * @returns {Promise<FolderPasswordInfo>} A promise that resolves to the folder password information.
   */
  createFolderPassword(
    folderId: number,
    payload: SetFolderPasswordPayload,
  ): Promise<FolderPasswordInfo> {
    return this.request(`/v3/folder/${folderId}/password`, 'POST', { ...payload });
  }

  /**
   * Updates the password settings for a folder.
   * @param folderId - The unique identifier of the folder.
   * @param payload - The password settings to update (all fields optional).
   * @returns {Promise<FolderPasswordInfo>} A promise that resolves to the updated folder password information.
   */
  updateFolderPassword(
    folderId: number,
    payload: UpdateFolderPasswordPayload,
  ): Promise<FolderPasswordInfo> {
    return this.request(`/v3/folder/${folderId}/password`, 'PUT', { ...payload });
  }

  /**
   * Deletes the password for a folder. Requires current password.
   * @param folderId - The unique identifier of the folder.
   * @param payload - The current password to authorize deletion.
   * @returns {Promise<DeleteFolderPasswordResponse>} A promise that resolves when the password is deleted.
   */
  deleteFolderPassword(
    folderId: number,
    payload: DeleteFolderPasswordPayload,
  ): Promise<DeleteFolderPasswordResponse> {
    return this.request(`/v3/folder/${folderId}/password`, 'DELETE', { ...payload });
  }

  /**
   * Gets the password information for a folder.
   * @param folderId - The unique identifier of the folder.
   * @returns {Promise<FolderPasswordInfo>} A promise that resolves to the folder password information.
   */
  getFolderPassword(folderId: number): Promise<FolderPasswordInfo> {
    return this.request(`/v3/folder/${folderId}/password`, 'GET');
  }

  /**
   * Checks if a folder has a password set.
   * @param folderId - The unique identifier of the folder.
   * @returns {Promise<CheckFolderPasswordResponse>} A promise that resolves to whether the folder has a password.
   */
  checkFolderPassword(folderId: number): Promise<CheckFolderPasswordResponse> {
    return this.request(`/v3/folder/${folderId}/password/check`, 'GET');
  }

  /**
   * Verifies a folder password.
   * @param folderId - The unique identifier of the folder.
   * @param payload - The password to verify.
   * @returns {Promise<VerifyFolderPasswordResponse>} A promise that resolves to the verification result.
   */
  verifyFolderPassword(
    folderId: number,
    payload: VerifyFolderPasswordPayload,
  ): Promise<VerifyFolderPasswordResponse> {
    return this.request(`/v3/folder/${folderId}/password/verify`, 'POST', { ...payload });
  }

  /**
   * Deletes a folder password session.
   * Calls DELETE /api/v3/folder/{folderId}/password/session with body { folderId, sessionId }.
   * @param folderId - The unique identifier of the folder.
   * @param sessionId - Session ID to delete. Required for single-session revoke (payload will include folderId and sessionId).
   * @returns {Promise<DeleteFolderPasswordSessionResponse>} A promise that resolves when the session is deleted.
   */
  deleteFolderPasswordSession(
    folderId: number,
    sessionId: number,
  ): Promise<DeleteFolderPasswordSessionResponse> {
    const url = `/v3/folder/${folderId}/password/session`;
    return this.request(url, 'DELETE', { folderId, sessionId });
  }

  /**
   * Gets all folder password sessions.
   * @returns {Promise<GetFolderPasswordSessionsResponse>} A promise that resolves to all folder password sessions.
   */
  getFolderPasswordSessions(): Promise<GetFolderPasswordSessionsResponse> {
    return this.request(`/v3/folder/password/sessions`, 'GET');
  }

  /**
   * Requests a reset password OTP for a folder.
   * @param fileId - The unique identifier of the folder.
   * @returns {Promise<any>} A promise that resolves to the reset password OTP.
   */
  requestResetPasswordOtp(fileId: number): Promise<any> {
    return this.request(`/v3/folder/${fileId}/password/request-reset-otp`, 'POST');
  }

  /**
   * Resets a folder password.
   * @param fileId - The unique identifier of the folder.
   * @param payload - The payload containing the OTP and new password.
   * @returns {Promise<FolderPasswordInfo>} A promise that resolves to the updated folder password information.
   */
  resetFolderPassword(
    fileId: number,
    payload: ResetFolderPasswordPayload,
  ): Promise<FolderPasswordInfo> {
    return this.request(`/v3/folder/${fileId}/password/reset-with-otp`, 'POST', {
      otpCode: payload.otpCode,
      newPassword: payload.newPassword,
    });
  }
}
